import { all, call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';

import has from 'lodash/has';

import {
  ADD_PAYMENT_METHOD,
  DELETE_PAYMENT_METHOD,
  GET_PAYMENT_METHODS
} from 'actions/paymentMethods/constants';
import {
  addPaymentMethodError,
  addPaymentMethodSuccess,
  deletePaymentMethodError,
  deletePaymentMethodSuccess,
  getPaymentMethods,
  getPaymentMethodsError,
  getPaymentMethodsSuccess
} from 'actions/paymentMethods';
import { hideModal } from 'actions/modal';

import normaliseError from 'utils/normaliseError';

import { HTTP_VERBS, STRIPE_REQUEST_TIMEOUT } from 'utils/constants';

/* Get payment methods */
export function* getPaymentMethodsSaga({ projectId }) {
  const requestUrl = `projects/${projectId}/cards`;
  const opts = {
    method: HTTP_VERBS.GET,
    url: requestUrl
  };

  try {
    const response = yield call(axios, opts);
    yield put(getPaymentMethodsSuccess(projectId, response.data.data));
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(getPaymentMethodsError(projectId, transformedError));
  }
}

export function* getPaymentMethodsWatcher() {
  yield takeLatest(GET_PAYMENT_METHODS, getPaymentMethodsSaga);
}

/*
 * Get Stripe client secret from our API
 * `intentType` is either `paymentIntent` or `setupIntent`
 * https://stripe.com/docs/api/payment_intents
 * https://stripe.com/docs/api/setup_intents
 */
export function* getStripeSecretSaga(projectId) {
  const requestUrl = `projects/${projectId}/setup_intents`;
  const opts = {
    method: HTTP_VERBS.POST,
    url: requestUrl,
    timeout: STRIPE_REQUEST_TIMEOUT
  };

  const { data } = yield call(axios, opts);
  return { clientSecret: data.data.client_secret };
}

/* Add payment method */
export function* addPaymentMethodSaga({ projectId, handleCardSetup, actions }) {
  try {
    const { clientSecret } = yield call(getStripeSecretSaga, projectId);

    // Now we have a client secret and the card data, we can call `handleCardSetup`
    // and (hopefully) get a `setupIntent` object from Stripe.
    // At this stage, the 3D Secure auth process may be triggered.
    // https://stripe.com/docs/stripe-js/reference#stripe-handle-card-setup
    const { error, setupIntent } = yield call(handleCardSetup, clientSecret);

    // Catch errors with Stripe `handleCardSetup`
    if (error || !setupIntent) {
      throw error;
    }

    // Call our endpoint with the token generated by Stripe
    const requestUrl = `projects/${projectId}/cards`;
    const addCardopts = {
      method: HTTP_VERBS.POST,
      url: requestUrl,
      data: JSON.stringify({
        payment_method_id: setupIntent.payment_method
      })
    };

    yield call(axios, addCardopts);
    yield put(addPaymentMethodSuccess(projectId));
    // Once we have succeeded in adding a new card, list payment methods
    yield put(getPaymentMethods(projectId));
    yield put(hideModal());
    yield call(toastr.success, 'Payment method added');
  } catch (error) {
    const { message } = error; // Stripe error
    // If there's a message from stripe, we return that, otherwise default
    const defaultMessage =
      message || 'There was a problem adding your payment option';
    const status = has(error, 'response.status') && error.response.status;
    // We pass the error from our API only if 402
    const filteredError = status === 402 && error;
    const transformedError = normaliseError(filteredError, defaultMessage);

    yield put(addPaymentMethodError(projectId, transformedError));

    if (actions) {
      yield call(actions.setErrors, {
        description: transformedError.description
      });
      yield call(actions.setSubmitting, false);
    }
  }
}

export function* addPaymentMethodWatcher() {
  yield takeLatest(ADD_PAYMENT_METHOD, addPaymentMethodSaga);
}

/* Delete payment method */
export function* deletePaymentMethodSaga({ projectId, paymentMethodId }) {
  const requestUrl = `projects/${projectId}/cards/${paymentMethodId}`;
  const opts = {
    method: HTTP_VERBS.DELETE,
    url: requestUrl
  };

  try {
    yield call(axios, opts);
    yield put(deletePaymentMethodSuccess(projectId, paymentMethodId));
    yield put(getPaymentMethods(projectId));
    yield call(toastr.success, 'Payment option deleted');
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(
      deletePaymentMethodError(projectId, paymentMethodId, transformedError)
    );
    yield call(
      toastr.error,
      'There was a problem deleting this payment option'
    );
  }
}

export function* deletePaymentMethodWatcher() {
  yield takeLatest(DELETE_PAYMENT_METHOD, deletePaymentMethodSaga);
}

export default function* rootSaga() {
  yield all([
    call(getPaymentMethodsWatcher),
    call(addPaymentMethodWatcher),
    call(deletePaymentMethodWatcher)
  ]);
}
